/* eslint-disable no-underscore-dangle */
import { CartProductItem } from '@westwing/ui-kit/ContentFlyout/CartFlyout/types';
import { clubProductUrl } from '@westwing/ui-kit/utils';
import { formatPrice } from '@westwing/appshell-formatting-helpers';
import { calculateImageHeight, formatImage } from '@westwing/ui-kit/utils/image';
import { CartAPIFullData, CartAPIItem, ClubProductData, ShopProductData } from '../types/oneCartAPI';
import { getShopStaticCmsHost } from './locationHelpers';

export interface TransformedCartProductItem extends CartProductItem {
    transformed: boolean;
}

const cartItemImage2XWidth = 250;

// TODO Use from ui-kit: https://westwing.jira.com/browse/CHECKOUT-2592
const isDTLImage = (url: string) => !url.includes('/e_trim/');

const transformShopProduct = (product: CartAPIItem, country: string): TransformedCartProductItem => {
    const { productData, origin, sku, stockData } = product;

    const { configData, simpleData } = productData as ShopProductData;
    const originalPrice = simpleData?.meta?.price?.value;
    const currentPrice = (simpleData.meta.special_price ?? simpleData.meta.price).value;
    const imageHeight = calculateImageHeight(cartItemImage2XWidth);
    const defaultImageSrc = simpleData.meta._images.original;
    const src = simpleData.meta._images.image_path;
    const isDTL = isDTLImage(defaultImageSrc);
    const staticCmsHost = getShopStaticCmsHost();

    const paddingSize = {
        top: 35,
        right: 0,
        bottom: 35,
        left: 0,
    };

    const imageConfig = {
        src,
        imageWidth: cartItemImage2XWidth,
        imageHeight,
        isTrimmed: !isDTL,
        padding: !isDTL,
        isClubItem: false,
        staticCmsHost,
        ...(!isDTL && { paddingSize }),
    };
    const { jpegSrc: jpegSrc2X } = formatImage(imageConfig);

    return {
        origin,
        sku,
        brand: configData.brand,
        name: configData.name,
        quantity: stockData.selected,
        stock: simpleData.meta.quantity,
        description: configData.description,
        image: jpegSrc2X,
        imagePath: jpegSrc2X,
        imageTag: simpleData.meta._images.tag as CartProductItem['imageTag'],
        originalPrice: originalPrice ? formatPrice(country, simpleData.meta.price.currency, originalPrice) : '',
        productUrl: `/${configData.pdp_url}`,
        price: formatPrice(country, simpleData.meta.price.currency, currentPrice),
        transformed: true,
    };
};

const transformClubProduct = (item: CartAPIItem, currency: string, country: string): TransformedCartProductItem => {
    const IMAGE_URL_PREFIX = 'image/upload';
    const IMAGE_TRANSFORMATION_VALUE = 't_default';
    const PRODUCT_IMAGE_SIZE = 'product_small';
    const productData = item.productData as ClubProductData;
    let imageUrl = '';
    if (productData.images && productData.images.length) {
        const mainImage = productData.images.filter(image => image.main === '1')[0];
        if (Object.keys(mainImage).length > 0) {
            const countryCode = country.toLocaleLowerCase() || 'de';
            imageUrl += `${IMAGE_URL_PREFIX}/${IMAGE_TRANSFORMATION_VALUE}.${PRODUCT_IMAGE_SIZE}`;
            imageUrl += `/v${mainImage.image_version}`;
            imageUrl += `/club/${countryCode}/product`;
            imageUrl += `/${mainImage.sku_supplier_config}`;
            imageUrl += `/${mainImage.image}`;
        }
    }
    const { meta } = productData;
    const simple = productData.simples[item.sku].meta;

    return {
        origin: item.origin,
        sku: meta.sku,
        brand: meta.brand,
        name: meta.name,
        quantity: item.stockData.selected,
        stock: item.stockData.selected.toString(),
        description: meta.description,
        image: imageUrl,
        imagePath: imageUrl,
        imageTag: 'reg',
        originalPrice: formatPrice(country, currency, Number(simple?.original_price)),
        productUrl: `/c/${clubProductUrl(
            productData.meta.name,
            productData.meta.config_id,
            productData.campaign.url_key
        )}`,
        price: formatPrice(country, currency, Number(simple?.price)),
        transformed: true,
    };
};

export const getCartProducts = (data: CartAPIFullData['data'] | undefined, country: string): CartProductItem[] => {
    if (!data?.cart?.itemList) {
        return [];
    }

    const { itemList, summary } = data.cart;

    return itemList.map(item => {
        const productDetails = item as CartAPIItem;
        if (item.origin === 'club' && !productDetails?.transformed) {
            return transformClubProduct(item as CartAPIItem, summary.grandTotal.currency, country);
        }
        if (item.origin === 'shop' && !productDetails?.transformed) {
            return transformShopProduct(item as CartAPIItem, country);
        }
        return item as CartProductItem;
    });
};

export const getGrandTotal = (
    summary: CartAPIFullData['data']['cart']['summary'] | undefined,
    country: string
): string => {
    if (!summary) {
        return '';
    }

    return formatPrice(country, summary.grandTotal.currency, summary.grandTotal.value / 100);
};
